import { Trans } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { AlertCircle } from '@untitledui/icons/AlertCircle';
import { CheckCircle } from '@untitledui/icons/CheckCircle';
import { Loading02 } from '@untitledui/icons/Loading02';
import { useEffect, useState } from 'react';

import { LinkButton } from '@/components/base/buttons/button';
import { BASE_PATH } from '@/constants/config';
import { useConsumeWorkEmailVerificationReturnPath } from '@/hooks/use-consume-work-email-verification-return-path';
import { AuthLayout } from '@/routes/participants/-components/auth-layout';
import { DecorativeCircles } from '@/routes/participants/-components/decorative-circles';
import { SignupCarousel } from '@/routes/participants/-components/signup-carousel';
import { serverFetch } from '@/utils/server-fetch';

const verifyWorkEmailToken = createServerFn()
  .validator(
    (data: { userId: string; profileId: string; token: string }) => data,
  )
  .handler(async ({ data: { userId, profileId, token } }) => {
    try {
      const response = await serverFetch({
        path: `/v4/profiles/${profileId}/verify/work-email/${token}`,
        method: 'PATCH',
        isServiceAdmin: true,
        userId,
      });

      if (!response.ok) {
        return { success: false as const };
      }

      return { success: true as const };
    } catch (error) {
      console.error('Work email verification error:', error);
      return { success: false as const };
    }
  });

export const Route = createFileRoute(
  '/participants/verify-work-email/$userId/$profileId/$token',
)({
  component: VerifyWorkEmailPage,
});

type VerifyState = 'verifying' | 'success' | 'error';

const FALLBACK_RETURN_PATH = `${BASE_PATH}/participants/settings?tab=professional-background`;

function VerifyWorkEmailPage() {
  const { userId, profileId, token } = Route.useParams();
  const consumeReturnPath = useConsumeWorkEmailVerificationReturnPath();
  const [state, setState] = useState<VerifyState>('verifying');

  useEffect(() => {
    let cancelled = false;
    let redirectTimeout: ReturnType<typeof setTimeout> | undefined;

    const run = async () => {
      try {
        const result = await verifyWorkEmailToken({
          data: { userId, profileId, token },
        });
        if (cancelled) return;
        if (result.success) {
          const nextReturnPath = consumeReturnPath() ?? FALLBACK_RETURN_PATH;
          setState('success');

          redirectTimeout = setTimeout(() => {
            if (!cancelled) {
              globalThis.location.assign(nextReturnPath);
            }
          }, 1500);
        } else {
          setState('error');
        }
      } catch {
        if (cancelled) return;
        setState('error');
      }
    };
    void run();

    return () => {
      cancelled = true;
      if (redirectTimeout) clearTimeout(redirectTimeout);
    };
  }, [userId, profileId, token, consumeReturnPath]);

  return (
    <AuthLayout rightPanel={<SignupCarousel />}>
      <div className="flex flex-col items-center text-center">
        <DecorativeCircles>
          {state === 'verifying' && (
            <div className="relative z-10 flex size-14 items-center justify-center rounded-full bg-brand-solid">
              <Loading02 className="size-7 animate-spin text-white" />
            </div>
          )}

          {state === 'success' && (
            <div className="relative z-10 flex size-14 items-center justify-center rounded-full bg-success-solid duration-500 animate-in fade-in zoom-in-50">
              <CheckCircle className="size-7 text-white" />
            </div>
          )}

          {state === 'error' && (
            <div className="relative z-10 flex size-14 items-center justify-center rounded-full bg-error-solid">
              <AlertCircle className="size-7 text-white" />
            </div>
          )}
        </DecorativeCircles>

        <h1 className="z-10 mt-2 font-heading text-display-sm font-semibold text-primary">
          {state === 'verifying' && <Trans>Verifying your work email...</Trans>}
          {state === 'success' && <Trans>Work email verified!</Trans>}
          {state === 'error' && <Trans>Verification failed</Trans>}
        </h1>

        <p className="z-10 mt-3 text-md text-primary">
          {state === 'verifying' && (
            <Trans>Please wait while we verify your work email address.</Trans>
          )}
          {state === 'success' && (
            <Trans>
              Your work email has been successfully verified. Redirecting you
              back...
            </Trans>
          )}
          {state === 'error' && (
            <Trans>
              We couldn&apos;t verify your work email. The link may have expired
              or already been used. Please try again from your settings page.
            </Trans>
          )}
        </p>

        {state === 'error' && (
          <div className="z-10 mt-8">
            <LinkButton
              color="primary"
              size="lg"
              to="/participants/settings"
              search={{ tab: 'professional-background' }}
            >
              <Trans>Go to settings</Trans>
            </LinkButton>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}
